/**
 * Zoho Marketing Automation Helper via PageSense
 * 
 * PageSense is Zoho's unified tracking platform for both analytics and Marketing Automation.
 * We use it to identify users in the browser so Zoho MA can match backend goal tracking
 * to the correct browser session for personalized experiences (popups, journeys, etc.)
 */

declare global {
  interface Window {
    pagesense?: any;
  }
}

/**
 * Identify a user in Zoho MA via PageSense
 * This connects the browser session to the contact email so Zoho MA can show
 * personalized content and track the user's journey
 * 
 * @param email - Contact email address (must match email used in backend goal tracking)
 * @param userData - Optional additional user properties (name, etc.)
 */
export const identifyUser = (email: string, userData?: Record<string, any>) => {
  console.log('🔵 Zoho MA/PageSense: identifyUser called with:', { email, userData });
  
  // Check if PageSense script is loaded
  if (typeof window.pagesense === 'undefined') {
    console.error('❌ Zoho MA/PageSense: Script not loaded!');
    console.error('❌ Check if PageSense script is in index.html');
    console.error('❌ Expected script: https://cdn.pagesense.io/js/getmulch/...');
    return;
  }

  console.log('✅ Zoho MA/PageSense: Script detected');
  console.log('🔍 PageSense object type:', typeof window.pagesense);
  console.log('🔍 Available methods:', window.pagesense && typeof window.pagesense === 'object' ? Object.keys(window.pagesense) : 'N/A');
  
  try {
    // Initialize pagesense array if not exists
    window.pagesense = window.pagesense || [];
    
    console.log('🟢 Zoho MA/PageSense: Identifying user with email:', email);
    
    // PageSense command for Marketing Automation integration
    // This links the browser session to the MA contact
    window.pagesense.push(['identifyUser', email]);
    console.log('✅ Zoho MA/PageSense: User identified via identifyUser command');
    
    // Also set custom attributes if provided
    if (userData && Object.keys(userData).length > 0) {
      console.log('🟢 Zoho MA/PageSense: Setting custom attributes:', userData);
      Object.entries(userData).forEach(([key, value]) => {
        window.pagesense.push(['setCustomAttribute', key, value]);
      });
      console.log('✅ Zoho MA/PageSense: Custom attributes set');
    }
    
    console.log('✅ Zoho MA/PageSense: User identification process complete for:', email);
  } catch (error) {
    console.error('❌ Zoho MA/PageSense: Identification failed:', error);
    console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
    if (error instanceof Error && error.stack) {
      console.error('❌ Stack trace:', error.stack);
    }
  }
};

/**
 * Track a goal/event in Zoho Marketing Automation via PageSense
 * Uses the exact script provided by Zoho MA: window.pagesense.push(['trackEvent', eventName])
 * 
 * @param eventName - Name of the event/goal to track (e.g., 'quote_accepted')
 */
export const trackGoal = (eventName: string) => {
  console.log('🔵 Zoho MA/PageSense: trackGoal called with event:', eventName);
  
  // Check if PageSense script is loaded
  if (typeof window.pagesense === 'undefined') {
    console.error('❌ Zoho MA/PageSense: Script not loaded!');
    console.error('❌ Cannot track goal without PageSense');
    return;
  }

  console.log('✅ Zoho MA/PageSense: Script detected');
  
  try {
    // Initialize pagesense array if not exists
    window.pagesense = window.pagesense || [];
    
    // Use the exact format provided by Zoho MA
    console.log('🔍 Tracking event using: window.pagesense.push(["trackEvent", "' + eventName + '"])');
    window.pagesense.push(['trackEvent', eventName]);
    console.log('✅ Zoho MA/PageSense: Event tracked successfully:', eventName);
  } catch (error) {
    console.error('❌ Zoho MA/PageSense: Goal tracking failed:', error);
    console.error('❌ Error details:', error instanceof Error ? error.message : String(error));
  }
};

/**
 * Track a quote acceptance goal
 * Convenience method for tracking quote acceptance events
 * Note: Zoho MA's trackEvent doesn't accept additional properties,
 * so we just track the event name. Properties should be set via identifyUser.
 */
export const trackQuoteAcceptance = () => {
  trackGoal('quote_accepted');
};
